import objectPath from 'object-path'
import httpClient from 'utility/httpClient.util'
import { getApi, mapResponseData, setApi, setHeadersConfig } from './Api'
import {
  updateInputValue,
  updateFormDataValue,
  getCurrentUi,
  getCheckValue,
  getDefaultValueFromUi,
  hasDefaultValueConfig,
  isNumber,
  updateUiSchema,
  getAllKeys,
  generateString,
} from './Data'
import {
  formSchema,
  formUischema,
  pageSchema,
  pageUischema,
  widgetSchema,
  widgetUischema,
  dashboardSchema,
  dashboardUischema,
} from './Schema'
import { staticContainer } from 'designer/component/system/menu/formElements/data/container'
import {
  basicElement,
  systemElement,
} from 'designer/component/system/menu/formElements/data/element'
import { staticWidget } from 'designer/component/system/menu/formElements/data/widget'
import { staticComponent } from 'designer/component/system/menu/formElements/data/component'
import { checkConditionApi } from './Api'
import cloneDeep from 'clone-deep'
import { debounceCallback } from 'utility/debounceCallback.util'
import dayjs from 'dayjs'
import { createApiGroupList } from './ApiOptimize'
import {
  applyRequestTimeout,
  getDesignerRequestVersion,
  isDesignerRequestVersionActive,
  withRequestTimeout,
} from './RequestTimeout'
import { applyRequireFieldConfig } from './Config'
import { getStorage } from './Storage'
import { requestFormDesign } from './FormDesignRequest'

const makeUppercaseFirst = (item) => {
  return item.charAt(0).toUpperCase() + item.slice(1)
}

const getDestinationLayoutKey = (jsonForm, current, destination) => {
  let destinationKey = objectPath.get(destination, 'droppableId')
  let destinationMapKey = objectPath.get(
    jsonForm,
    `schema.mapKey.${destinationKey}`,
  )
  let destinationLayout = destinationMapKey?.split('.')?.filter(Boolean)?.[0]

  return destinationLayout || current?.ui?.layout || current?.ui?.key
}

export const loadRouterHistory = (
  routerHistory,
  location,
  formData,
  jsonForm,
) => {
  const stateData = objectPath.get(location, 'state') || []
  if (routerHistory) {
    routerHistory.forEach(
      ({ prop, propType, dynamicName, key, jsonKey, value }) => {
        let fullPropKey = jsonKey ? [prop, jsonKey].join('.') : prop
        let data = stateData.find((state) => state.fullPropKey === fullPropKey)
        if (data) {
          let value = objectPath.get(data, 'value')
          if (propType === 'input') {
            objectPath.set(formData, dynamicName, value)
            objectPath.set(formData, key, value)
          } else if (propType === 'table') {
            objectPath.set(formData, prop, value)
          } else if (propType === 'formData') {
            let formGroup = objectPath.get(jsonForm, 'formGroup')
            let source = objectPath.get(data, 'value')
            formData = updateFormDataValue({
              jsonForm,
              formGroup: formGroup,
              formDataName: value,
              formDataKey: key,
              source: source,
              destination: formData,
            })
          }
        }
      },
    )
  }

  return formData
}

export const loadSearchToFormData = async (
  searchApi,
  searchData,
  setFormData,
  updateUi,
) => {
  let { formData } = searchData
  if (!searchApi) return formData
  let response = {}
  let data = null
  let status = null
  let message = ''
  let api = getApi(searchApi, searchData, 'loadSearchToFormData')
  if (!api) return formData
  let config = setHeadersConfig(searchApi)
  response = await httpClient
    .get(api, config)
    .then((data) => data)
    .catch(function (error) {
      let errorResponse = {
        status: 500,
        message: 'There was an error!',
        data: null,
      }
      if (error.response) {
        errorResponse = {
          status: error.response.status,
          message: error.response.data,
          data: null,
        }
      }
      return errorResponse
    })
  data = response.data
  message = response.message
  status = response.status

  if (status >= 400) return formData
  if (!data) return formData

  // has data key
  if (searchApi.dataKey) {
    data = objectPath.get(data, searchApi.dataKey)
  }

  let responseTable = searchApi?.dataTable || []
  responseTable.forEach(
    ({
      response,
      key,
      dynamicName,
      isDynamic,
      isComponentData,
      componentName,
    }) => {
      let value = response ? objectPath.get(data, response) : null
      if (isDynamic) {
        objectPath.set(formData, dynamicName, value)
      } else if (isComponentData && componentName) {
        objectPath.set(formData, `${componentName}.${response}`, value)
      } else if (key && response) {
        objectPath.set(formData, key, value)
        objectPath.set(formData, response, value)
      } else if (response) {
        objectPath.set(formData, response, value)
      }
    },
  )
  if (setFormData) {
    setFormData(formData)
  }
  if (updateUi) {
    updateUi()
  }
  return formData
}

export const createOptionResponse = (optionTable, data, jsonForm) => {
  optionTable.forEach(
    ({ systemKey, response, optionId, optionLabel, optionValue }) => {
      let value = objectPath.get(data, response)
      if (!value) return

      let ui = getCurrentUi(systemKey, jsonForm.schema, jsonForm.uischema)
      let keyOptions = []
      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          let sampleData = objectPath.get(value, 0) || []
          keyOptions = getAllKeys(sampleData)
        } else {
          keyOptions = getAllKeys(value)
        }
      }
      if (ui.key === systemKey) {
        let { tagname, elements = [] } = ui

        if (tagname === 'select') {
          if (!ui?.optionType) {
            objectPath.set(ui, 'optionType', 'array')
          }
          objectPath.set(ui, 'array', {
            options: value,
            textarea: JSON.stringify(value),
            keyOptions,
            optionLabel,
            optionValue,
          })
          jsonForm.uischema = updateUiSchema(systemKey, '', ui, jsonForm)
        } else if (tagname === 'panelGroup') {
          let newPanelElements = []
          if (Array.isArray(value)) {
            value.forEach((item, index) => {
              let panelElement = cloneDeep(elements[0])
              let title = objectPath.get(item, optionLabel)

              let { key, name } = panelElement
              let mainKey = cloneDeep(key)
              let mainName = cloneDeep(name)
              let subElements = cloneDeep(panelElement.elements)

              subElements.map((element) => {
                let { tagname, optionType, json = {} } = element
                let { checkboxData } = json
                if (keyOptions.includes(checkboxData)) {
                  if (tagname === 'checkboxGroup' && optionType === 'json') {
                    let {
                      checkboxData,
                      checkboxName,
                      checkboxLabel,
                      valueChecked,
                      valueUnchecked,
                    } = json
                    let options = objectPath.get(item, checkboxData) || []
                    let checkboxOptions = []
                    options.forEach((item) => {
                      let name = objectPath.get(item, checkboxName)
                      let label = objectPath.get(item, checkboxLabel)
                      checkboxOptions.push({
                        key: name,
                        name: name,
                        label: label,
                        formData: true,
                        defaultChecked: false,
                        valueChecked,
                        valueUnchecked,
                        '@value': item,
                      })
                    })
                    element.options = checkboxOptions
                  }
                }
                element.isCloneElement = index > 0
                return element
              })

              if (index > 0) {
                key = key + '_' + index
                name = name + '_' + index
              }
              panelElement = {
                ...panelElement,
                title,
                key,
                name,
                mainKey,
                mainName,
                optionId: objectPath.get(item, optionId),
                elements: subElements,
                isCloneElement: index > 0,
              }
              newPanelElements.push(panelElement)
            })
            objectPath.set(ui, 'elements', newPanelElements)
          }
        }
      }
    },
  )
}

const getCheckboxInputDefaultUi = (key, elements = []) => {
  let defaultUi
  elements.forEach((ui) => {
    if (defaultUi) return
    let input = objectPath.get(ui, 'input')
    let inputKey = objectPath.get(input, 'key')
    let inputName = objectPath.get(input, 'name')
    if (
      ui.tagname === 'checkboxInput' &&
      (inputKey === key || inputName === key)
    ) {
      defaultUi = input
      return
    }
    let childElements = objectPath.get(ui, 'elements') || []
    if (childElements.length > 0) {
      defaultUi = getCheckboxInputDefaultUi(key, childElements)
    }
  })
  return defaultUi
}

const isEmptySelectDefaultValue = (value) => {
  if (value === '' || value === undefined || value === null) return true
  if (Array.isArray(value) && value.length === 0) return true
  if (typeof value === 'object' && Object.keys(value).length === 0) return true

  return false
}

export const setDefaultValueToFormData = (
  jsonForm = {},
  auth = {},
  isForceConfigValue = false,
) => {
  let mapFormData = objectPath.get(jsonForm, 'schema.mapFormData') || {}
  let formData = objectPath.get(jsonForm, 'formData') || {}
  let schema = objectPath.get(jsonForm, 'schema') || {}
  let uischema = objectPath.get(jsonForm, 'uischema') || {}
  if (!schema.mapKey || !uischema.elements) return jsonForm
  Object.keys(mapFormData).forEach((key) => {
    if (!mapFormData[key]) return
    let value = objectPath.get(formData, key)
    let isForceValue =
      isForceConfigValue &&
      hasDefaultValueConfig(key, auth, jsonForm.formCode)
    let ui = getCurrentUi(key, schema, uischema)
    if (!ui || ui.key !== key) {
      ui = getCheckboxInputDefaultUi(key, uischema.elements)
    }
    if (!ui || ui.key !== key) return
    let defaultValue = getDefaultValueFromUi(ui, key, jsonForm, auth)
    if (defaultValue === undefined) return
    let hasMapResponse =
      objectPath.get(formData, ['@mapResponse', key]) ||
      objectPath.get(formData, ['@mapResponse', ui.key]) ||
      objectPath.get(formData, ['@mapResponse', ui.name])
    let isEmptySelectDefault =
      ui.tagname === 'select' &&
      !hasMapResponse &&
      !isEmptySelectDefaultValue(defaultValue) &&
      isEmptySelectDefaultValue(value)
    if (value !== undefined && !isForceValue && !isEmptySelectDefault) return
    objectPath.set(formData, key, defaultValue)
    if (ui.name && objectPath.get(formData, ui.name) === undefined) {
      objectPath.set(formData, ui.name, defaultValue)
    }
  })
  objectPath.set(jsonForm, 'formData', formData)
  return jsonForm
}

const isEmptyRadioValue = (value) => {
  if (value === '' || value === undefined || value === null) return true
  if (Array.isArray(value) && value.length === 0) return true
  if (typeof value === 'object' && Object.values(value).length === 0) {
    return true
  }
  return false
}

const getCompareRadioValue = (value) => (isNumber(value) ? Number(value) : value)

const walkUiSchemaElements = (elements = [], callback) => {
  elements.forEach((element) => {
    callback(element)
    if (Array.isArray(element.elements)) {
      walkUiSchemaElements(element.elements, callback)
    }
  })
}

const getDefaultRadioValue = (
  jsonForm = {},
  formData = {},
  uischema = {},
  radio = {},
) => {
  let { radioName } = radio
  let name = radioName ?? uischema.name
  let radioValue = radioName
    ? objectPath.get(formData, radioName)
    : objectPath.get(formData, uischema.key)

  if (!radioName && isEmptyRadioValue(radioValue)) {
    radioValue = objectPath.get(formData, uischema.name)
  }
  if (isEmptyRadioValue(radioValue)) {
    let storage = getStorage(jsonForm, name)
    let value
    if (isEmptyRadioValue(storage)) {
      if (uischema.defaultChecked) {
        if (
          objectPath.get(uischema.defaultChecked, 'radioName') === radioName
        ) {
          value = uischema.defaultChecked.value
        }
      } else {
        value = uischema.defaultValue
      }
    } else {
      value = storage
    }
    radioValue = getCheckValue(value, uischema.format)
  }
  return radioValue
}

const applyRadioOptionAction = (elementAction = {}, option = {}) => {
  let { showField, hideField, readonlyField, readonly = false } =
    option.action || {}

  if (showField) {
    showField.forEach(({ key }) => {
      objectPath.set(elementAction, ['isShow', key], true)
    })
  }
  if (hideField) {
    hideField.forEach(({ key }) => {
      objectPath.set(elementAction, ['isShow', key], false)
    })
  }
  if (readonlyField) {
    readonlyField.forEach(({ key }) => {
      objectPath.set(elementAction, ['isEnable', key], !readonly)
    })
  }

  return elementAction
}

const applyDefaultRadioActions = (jsonForm = {}, elementAction = {}) => {
  let formData = objectPath.get(jsonForm, 'formData') || {}
  let elements = objectPath.get(jsonForm, 'uischema.elements') || []

  walkUiSchemaElements(elements, (uischema = {}) => {
    if (uischema.tagname !== 'radio') return
    let options = uischema.options || []
    let option = options.find((radio = {}) => {
      let radioValue = getDefaultRadioValue(jsonForm, formData, uischema, radio)
      return (
        getCompareRadioValue(radioValue) === getCompareRadioValue(radio.value)
      )
    })
    if (option?.action) {
      applyRadioOptionAction(elementAction, option)
    }
  })

  return elementAction
}

const isDependentConditionApi = (api = {}) => {
  if (objectPath.get(api, 'condition.response')) return true
  if (objectPath.get(api, 'condition.datasource')) return true

  let params = [...(api.pathParams || []), ...(api.queryParams || [])]
  return params.some(({ propType }) => propType === 'datasource')
}

const createConditionApiBatchList = (apiGroup = []) => {
  let batchList = []
  let batch = []

  apiGroup.forEach((api) => {
    if (isDependentConditionApi(api)) {
      if (batch.length > 0) batchList.push(batch)
      batchList.push([api])
      batch = []
      return
    }
    batch.push(api)
  })
  if (batch.length > 0) batchList.push(batch)

  return batchList
}

const loadConditionApiBatch = async (
  batch,
  jsonForm,
  formData,
  requestData,
  setDatasourceValue,
) => {
  const requestVersion = getDesignerRequestVersion()
  let { urlParams, datasourceValue = {}, isSearchApi, designContext } =
    requestData

  let promiseList = batch.map(async (item) => {
    if (!isSearchApi) {
      let isGet = checkConditionApi(item, urlParams, formData, datasourceValue)
      if (!isGet) return null
    }
    let api = setApi({ ...item, jsonForm }, formData, requestData)
    if (!api) return null

    let { headers = [] } = item
    let config = {}
    if (headers.length > 0) {
      headers.forEach(({ key, value }) => {
        if (key) objectPath.set(config, `headers.${key}`, value)
      })
    }
    config = applyRequestTimeout(config, item)

    let response = await withRequestTimeout(httpClient.get(api, config), item)
      .then((data) => data)
      .catch(function (error) {
        let errorResponse = {
          status: 500,
          message: 'There was an error!',
          data: null,
        }
        if (error.response) {
          errorResponse = {
            status: error.response.status,
            message: error.response.data,
            data: null,
          }
        }
        return errorResponse
      })
    if (!response || !isDesignerRequestVersionActive(requestVersion)) return null
    if (response.status >= 400) {
      console.error('There was an error!', { item, response, formData })
      return null
    }
    return { api, item, response }
  })

  let responseList = await Promise.all(promiseList)
  if (!isDesignerRequestVersionActive(requestVersion)) return formData

  let batchSearchData = designContext?.searchData
  let batchDesignContext = designContext
  if (designContext?.setSearchData) {
    batchDesignContext = {
      ...designContext,
      setSearchData: (searchData) => {
        batchSearchData = searchData
      },
    }
  }

  responseList.forEach((res) => {
    if (!res) return
    let { api, item, response } = res
    let data = response.data
    if (batchDesignContext) {
      batchDesignContext.searchData = batchSearchData
    }
    formData = mapResponseData(
      { ...item, api },
      data,
      formData,
      {},
      jsonForm,
      batchDesignContext,
    )
    objectPath.set(formData, `@response.${item.key}`, response.data)
    if (item.dataKey) data = objectPath.get(data, item.dataKey)
    objectPath.set(datasourceValue, item.key, data)
  })
  if (
    designContext?.setSearchData &&
    batchSearchData !== designContext.searchData
  ) {
    designContext.setSearchData(batchSearchData)
  }
  if (setDatasourceValue) setDatasourceValue(datasourceValue)

  return formData
}

const loadConditionApiToFormData = async (
  apiGroup,
  jsonForm,
  formData,
  requestData,
  setDatasourceValue,
) => {
  let batchList = createConditionApiBatchList(apiGroup)
  for (let batch of batchList) {
    formData = await loadConditionApiBatch(
      batch,
      jsonForm,
      formData,
      requestData,
      setDatasourceValue,
    )
  }
  return formData
}

export const loadApiToFormData = async (
  jsonForm,
  formData,
  requestData,
  apiGroup = null,
  isFromCondition = false,
  setDatasourceValue,
) => {
  const requestVersion = getDesignerRequestVersion()
  let {
    urlParams,
    designerData = {},
    datasourceValue = {},
    isSearchApi,
    designContext,
  } = requestData
  if (!apiGroup) {
    apiGroup = objectPath.get(jsonForm, 'apiGroup') || []
  }
  if (!Array.isArray(apiGroup) || apiGroup.length === 0) return formData
  apiGroup = createApiGroupList(apiGroup)

  if (isFromCondition) {
    return loadConditionApiToFormData(
      apiGroup,
      jsonForm,
      formData,
      requestData,
      setDatasourceValue,
    )
  }

  let promiseList = []
  let allData = { ...formData }
  apiGroup.forEach((item) => {
    if (item?.condition?.condition) {
      if (!isFromCondition) return
    }
    let promise = (async () => {
      let response = {}
      let data = null
      let status = null
      let message = ''
      let awaitPromised = await Promise.all(promiseList)
      awaitPromised.forEach((data) => {
        if (data?.formData) {
          allData = data?.formData
        } else {
          allData = formData
        }
      })
      if (!isSearchApi) {
        let isGet = checkConditionApi(item, urlParams, allData, datasourceValue)
        if (!isGet) return Promise.reject({ item, formData: allData })
      }
      let api = setApi({ ...item, jsonForm }, formData, requestData)
      if (!api) return formData
      let { headers = [] } = item
      let config = {}
      if (headers.length > 0) {
        headers.forEach(({ key, value }) => {
          if (key) {
            objectPath.set(config, `headers.${key}`, value)
          }
        })
      }
      config = applyRequestTimeout(config, item)
      response = await withRequestTimeout(httpClient.get(api, config), item)
        .then((data) => data)
        .catch(function (error) {
          let errorResponse = {
            status: 500,
            message: 'There was an error!',
            data: null,
          }
          if (error.response) {
            errorResponse = {
              status: error.response.status,
              message: error.response.data,
              data: null,
            }
          }
          return errorResponse
        })
      if (!response) return Promise.reject({ item, formData, isCancel: true })
      if (!isDesignerRequestVersionActive(requestVersion)) {
        return Promise.reject({ item, formData, isCancel: true })
      }
      data = response.data
      message = response.message
      status = response.status
      if (status >= 400) return Promise.reject({ data, item, response, formData })
      formData = mapResponseData(
        { ...item, api },
        data,
        formData,
        {},
        jsonForm,
        designContext,
      )
      return { data, item, response, formData }
    })()
      .then((values) => values)
      .catch((error) => {
        if (error?.isCancel) return null
        console.error('There was an error!', error)
      })
    promiseList.push(promise)
  })
  await Promise.all(promiseList).then((resolveData) => {
    if (!isDesignerRequestVersionActive(requestVersion)) return
    resolveData.forEach((res) => {
      if (res) {
        let { data = null, item, response } = res

        if (item) {
          if (item.dataKey) {
            data = objectPath.get(data, item.dataKey)
          }
          objectPath.set(datasourceValue, item.key, data)
          objectPath.set(formData, `@response.${item.key}`, response.data)
          if (setDatasourceValue) {
            setDatasourceValue(datasourceValue)
          }
        }
      }
    })
  })
  return formData
}

export const loadVirtualization = async (
  jsonForm,
  formData,
  urlParams,
  designerData = {},
  datasourceValue = {},
) => {
  let virtApi = objectPath.get(jsonForm, 'virtApi')
  if (!virtApi) return formData
  let api = setApi({ ...virtApi, jsonForm }, formData, {
    urlParams,
    designerData,
    datasourceValue,
  })
  if (!api) return formData
  let responseData = await httpClient
    .get(api)
    .then(({ data }) => data)
    .catch(() => null)

  // has data key
  if (virtApi.dataKey) {
    responseData = objectPath.get(responseData, virtApi.dataKey)
  }
  objectPath.set(formData, virtApi.key, responseData)
  return formData
}

export const loadUrlParamToFormData = (
  jsonForm,
  formData,
  pathParams,
  queryParams,
  datasourceValue = {},
) => {
  let formGroup = objectPath.get(jsonForm, 'formGroup') || []
  let receivePathParams = objectPath.get(jsonForm, 'uischema.pathParams') || []
  receivePathParams.forEach(({ key, param, propType }) => {
    let dataValue = objectPath.get(pathParams, param)
    if (propType === 'input') {
      let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
      formData = updateInputValue({
        destination: formData,
        key: key,
        value: dataValue,
        name: key === ui?.key ? ui.name : key,
        type: 'url-param',
      })
    } else if (propType === 'datasource') {
      objectPath.set(datasourceValue, `pathParam.${param}`, dataValue)
    } else if (propType === 'formData') {
      let formItem = formGroup.find((form) => form.key === key)
      if (formItem) {
        let { mapParam, mapArray, mapArrayData = {} } = formItem
        Object.keys(mapParam).forEach((key) => {
          let mainName = mapParam[key]
          if (mapArray[key]) {
            let childArray = mapArrayData[mainName] || {}
            Object.keys(childArray).forEach((childName) => {
              let subArray = childArray[childName] || {}
              Object.keys(subArray).forEach((subName) => {
                let subKey = subArray[subName]
                objectPath.set(formData, subKey, dataValue)
                objectPath.set(formData, `@${childName}.${subName}`, dataValue)
              })
            })
          } else {
            let name = mapParam[key]
            objectPath.set(formData, key, dataValue)
            objectPath.set(formData, name, dataValue)
          }
        })
      }
    }
  })
  let receiveQueryParams =
    objectPath.get(jsonForm, 'uischema.queryParams') || []
  receiveQueryParams.forEach(({ key, param, propType }) => {
    let dataValue = objectPath.get(queryParams, param)
    if (propType === 'input') {
      let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
      formData = updateInputValue({
        destination: formData,
        key: key,
        value: dataValue,
        name: key === ui?.key ? ui.name : key,
        type: 'url-param',
      })
    } else if (propType === 'datasource') {
      objectPath.set(datasourceValue, `queryParam.${param}`, dataValue)
    } else if (propType === 'formData') {
      let formItem = formGroup.find((form) => form.key === key)
      if (formItem) {
        let { mapParam, mapArray, mapArrayData = {} } = formItem
        Object.keys(mapParam).forEach((key) => {
          let mainName = mapParam[key]
          if (mapArray[key]) {
            let childArray = mapArrayData[mainName] || {}
            Object.keys(childArray).forEach((childName) => {
              let subArray = childArray[childName] || {}
              Object.keys(subArray).forEach((subName) => {
                let subKey = subArray[subName]
                objectPath.set(formData, subKey, dataValue)
                objectPath.set(formData, `@${childName}.${subName}`, dataValue)
              })
            })
          } else {
            let name = mapParam[key]
            objectPath.set(formData, key, dataValue)
            objectPath.set(formData, name, dataValue)
          }
        })
      }
    }
  })
  return formData
}

export const loadPropToFormData = (jsonForm, formData, location) => {
  let state = location.state || []
  if (state.length > 0) {
    let receiveProps = objectPath.get(jsonForm, 'uischema.props') || []
    let pageCode =
      state.find(
        (item) => item.prop === 'pageCode' && item.propType === 'system',
      ) || ''
    let filterProps = receiveProps.filter(
      (item) => item.pageCode === pageCode.value,
    )
    let formGroup = objectPath.get(jsonForm, 'formGroup')
    filterProps.forEach(
      ({ jsonKey = '', key = '', prop = '', propType = '', value = '' }) => {
        let found = state.find((item) => item.prop === prop)
        if (!found) return
        if (propType === 'input' || propType === 'condition') {
          let dataValue = objectPath.get(found.value, jsonKey)
          formData = updateInputValue({
            destination: formData,
            key: key,
            value: dataValue,
            type: 'prop',
          })
        } else if (propType === 'formData') {
          formData = updateFormDataValue({
            jsonForm,
            formGroup: formGroup,
            formDataName: value,
            formDataKey: key,
            source: found.value,
            destination: formData,
            type: 'prop',
          })
        }
      },
    )
  }
  return formData
}

export const loadComponentData = (
  receiveProps = [],
  {
    jsonForm,
    componentData = {},
    formData,
    datasourceValue,
    systemData = {},
    uischema,
  },
  formGroup = [],
) => {
  let uiFormData = uischema?.formData || {}
  let allData = { ...systemData, ...componentData, ...uiFormData }
  receiveProps.forEach(
    ({
      prop,
      key,
      propKey,
      propType,
      system = [],
      isDynamic,
      systemName,
      systemType,
      jsonKey,
      fullPropKey,
      systemValue,
      customType,
      dataType,
      dynamicName,
      systemKey,
    }) => {
      if (['callback', 'triggerButton'].includes(propType)) {
        return
      }
      if (isDynamic) {
        let value = objectPath.get(allData, [prop, systemName])
        objectPath.set(formData, systemName, value)
      } else if (dataType === 'input' && propType === 'input') {
        let value = objectPath.get(allData, prop)
        objectPath.set(formData, key, value)
        objectPath.set(formData, dynamicName, value)
      } else if (propType === 'formData') {
        let source = {}
        if (fullPropKey) {
          source = objectPath.get(allData, fullPropKey)
        } else if (objectPath.get(allData, [prop, jsonKey])) {
          source = objectPath.get(allData, [prop, jsonKey])
        } else if (objectPath.get(allData, prop)) {
          source = objectPath.get(allData, prop)
        } else {
          source = allData
        }
        formData = updateFormDataValue({
          jsonForm,
          formGroup: formGroup,
          formDataName: systemName,
          formDataKey: key || systemKey,
          source: source,
          destination: formData,
        })
      } else if (propType === 'datasource') {
        let value = ''
        if (fullPropKey) {
          value = objectPath.get(allData, fullPropKey)
        } else if (jsonKey) {
          value = objectPath.get(allData, [prop, jsonKey])
        } else {
          value = objectPath.get(allData, prop)
        }
        if (jsonKey) {
          objectPath.set(datasourceValue, `receiveProps.${jsonKey}`, value)
          objectPath.set(
            datasourceValue,
            `receiveProps.${prop}.${jsonKey}`,
            value,
          )
        } else {
          objectPath.set(datasourceValue, `receiveProps.${prop}`, value)
        }
      } else if (systemType === 'jsonData') {
        let value = objectPath.get(allData, 'jsonData')
        objectPath.set(formData, 'jsonData', value)
      } else if (systemType === 'custom') {
        if (customType === 'number') {
          systemValue = Number(systemValue)
        } else if (customType === 'array' || customType === 'object') {
          if (typeof systemValue === 'string') {
            systemValue = JSON.parse(systemValue)
          }
        }
        objectPath.set(formData, prop, systemValue)
      } else if (systemType === 'table') {
        if (jsonKey) {
          let value = objectPath.get(allData, jsonKey)
          objectPath.set(formData, key, value)
        } else {
          let value = objectPath.get(allData, prop)
          objectPath.set(formData, key, value)
        }
      } else if (systemType === 'replaceRow') {
      } else if (systemType === 'rowWaitReplace') {
      } else if (systemType === 'rowConfirmReplace') {
      } else if (systemType === 'tab') {
      } else {
        system.forEach(
          ({ systemKey, systemName, field, isDynamic, dynamicName }) => {
            let value = null
            if (propType === 'formData') {
              value =
                objectPath.get(allData, `${prop}.${systemName}`) ||
                objectPath.get(allData, `${prop}.${field}`)
            } else {
              value =
                objectPath.get(allData, systemKey) ||
                objectPath.get(allData, systemName) ||
                objectPath.get(allData, field)
            }
            if (value) {
              if (isDynamic) {
                objectPath.set(formData, dynamicName, value)
              } else {
                objectPath.set(formData, key, value)
                objectPath.set(formData, systemKey, value)
              }
            } else {
              objectPath.del(formData, systemKey)
            }
          },
        )
      }
    },
  )
  return formData
}

export const loadDesignerData = (
  receiveProps = [],
  { jsonForm, designerData, formData = {}, datasourceValue = {}, systemData },
  formGroup = [],
  modalDynamicIndex,
) => {
  let filterProps = receiveProps.filter(({ propReceive }) => propReceive)
  filterProps.forEach((item) => {
    let {
      prop,
      propKey,
      propType,
      system = [],
      isDynamic,
      systemName,
      systemType,
      systemKey,
      jsonKey,
      fullPropKey,
      systemValue,
    } = item
    let itemKey = ''
    if (modalDynamicIndex !== null && modalDynamicIndex > -1) {
      itemKey = [systemKey, modalDynamicIndex]
    } else if (fullPropKey) {
      itemKey = fullPropKey
    } else if (jsonKey) {
      itemKey = [prop, jsonKey].join('.')
    }
    if (['callback', 'triggerButton'].includes(propType)) {
      return
    }
    let value

    if (itemKey || jsonKey) {
      if (objectPath.has(systemData, itemKey)) {
        value = objectPath.get(systemData, itemKey)
      } else if (objectPath.has(systemData, jsonKey)) {
        value = objectPath.get(systemData, jsonKey)
      } else if (objectPath.has(designerData, `${systemKey}.${itemKey}`)) {
        value = objectPath.get(designerData, `${systemKey}.${itemKey}`)
      } else if (objectPath.has(designerData, itemKey)) {
        value = objectPath.get(designerData, itemKey)
      } else if (objectPath.has(designerData, jsonKey)) {
        value = objectPath.get(jsonKey, jsonKey)
      }
    } else {
      value =
        objectPath.get(designerData, prop) || objectPath.get(systemData, prop)
    }
    if (systemType === 'dynamic') {
      value = objectPath.get(
        systemData,
        `${item.fullPropKey}.${modalDynamicIndex}`,
      )
      // objectPath.set(formData, propKey, value)
      // objectPath.set(formData, jsonKey, value)
      if (propType === 'formData') {
        let source = value
        let formDataKey = propKey
        formData = updateFormDataValue({
          jsonForm,
          formGroup: formGroup,
          formDataName: '',
          formDataKey: formDataKey,
          source: source,
          destination: formData,
        })
      }
    } else if (systemType === 'custom') {
      objectPath.set(formData, itemKey, systemValue)
    } else if (systemType === 'apiResponse') {
      if (value) {
        let response
        if (jsonKey && objectPath.has(value, jsonKey)) {
          response = objectPath.get(value, jsonKey)
        } else if (objectPath.has(value, 'data')) {
          response = objectPath.get(value, 'data')
        } else {
          response = value
        }
        objectPath.set(formData, propKey, response)
      }
    } else if (systemType === 'tableResponse') {
      let data = objectPath.get(systemData, systemKey)
      objectPath.set(formData, fullPropKey, data)
    } else if (systemType === 'datasource') {
      value = objectPath.get(datasourceValue, systemKey)
      objectPath.set(formData, itemKey, value)
    } else if (systemType === 'jsonData') {
      let value = objectPath.get(designerData, 'jsonData')
      objectPath.set(formData, 'jsonData', value)
    } else if (systemType === 'custom') {
      let value = objectPath.get(designerData, prop)
      objectPath.set(formData, prop, value)
    } else if (systemType === 'table') {
      let propUi = getCurrentUi(propKey, jsonForm.schema, jsonForm.uischema)
      if (propUi.tagname === 'table') {
        if (!Array.isArray(value)) {
          value = [value]
        }
      }
      objectPath.set(formData, itemKey, value)
    } else if (systemType === 'replaceRow') {
    } else if (systemType === 'rowWaitReplace') {
    } else if (systemType === 'rowConfirmReplace') {
    } else if (systemType === 'system') {
      objectPath.set(formData, itemKey, value)
    } else if (systemType === 'tab') {
      objectPath.set(formData, itemKey, value)
    }
    if (propType === 'input') {
      if (jsonKey && objectPath.has(value, jsonKey)) {
        value = objectPath.get(value, jsonKey)
      }
      let propUi = getCurrentUi(propKey, jsonForm.schema, jsonForm.uischema)
      if (propKey === propUi.key) {
        objectPath.set(formData, propUi.key, value)
        objectPath.set(formData, propUi.name, value)
      }
      objectPath.set(formData, propKey, value)
    } else if (propType === 'formData') {
      let source = value
      let formDataKey = propKey
      formData = updateFormDataValue({
        jsonForm,
        formGroup: formGroup,
        formDataName: '',
        formDataKey: formDataKey,
        source: source,
        destination: formData,
      })
    } else if (propType === 'condition') {
      objectPath.set(formData, `@condition.${fullPropKey}`, 1)
    } else if (Array.isArray(system)) {
      system.forEach(
        ({ systemKey, systemName, field, isDynamic, dynamicName }) => {
          let value = null
          if (propType === 'formData') {
            value =
              objectPath.get(designerData, `${prop}.${systemName}`) ||
              objectPath.get(designerData, `${prop}.${field}`)
          } else {
            value =
              objectPath.get(designerData, systemKey) ||
              objectPath.get(designerData, systemName) ||
              objectPath.get(designerData, field)
          }
          if (value) {
            if (isDynamic) {
              objectPath.set(formData, dynamicName, value)
            } else {
              objectPath.set(formData, itemKey, value)
              objectPath.set(formData, systemKey, value)
            }
          } else {
            objectPath.del(formData, systemKey)
          }
        },
      )
    }
  })
  objectPath.set(datasourceValue, 'receiveProps', formData)
  return formData
}

export const createDesignerData = (
  designerPropsData = {},
  searchData,
  formData,
  props,
) => {
  let data = {}
  if (!props) {
    props = objectPath.get(designerPropsData, 'props') || []
  }
  let componentProps = props.filter(
    ({ propReceive, systemType }) =>
      propReceive && systemType !== 'triggerButton',
  )
  componentProps.forEach(
    ({
      system = [],
      prop,
      propKey,
      systemType,
      systemName,
      fullPropKey,
      systemKey,
      isDynamic,
      isSearch,
    }) => {
      if (isDynamic) {
        let value = null
        if (isSearch) {
          value = objectPath.get(searchData, systemName)
        } else {
          value = objectPath.get(formData, systemName)
        }
        objectPath.set(data, `${prop}.${systemName}`, value)
      } else if (systemType === 'table') {
        let value = null
        if (isSearch) {
          value =
            objectPath.get(searchData, systemKey) ||
            objectPath.get(searchData, system)
        } else {
          value =
            objectPath.get(formData, systemKey) ||
            objectPath.get(formData, system)
        }
        objectPath.set(data, `${prop}.${system}`, value)
      } else if (systemType === 'replaceRow') {
      } else if (systemType === 'files') {
        let value =
          objectPath.get(formData, systemKey) ||
          objectPath.get(formData, system)
        objectPath.set(data, `${prop}.${propKey}`, value)
      } else if (systemType === 'addFiles') {
        let oldFiles = objectPath.get(formData, systemKey) || []
        let newFiles = objectPath.get(data, propKey)
        let value = newFiles.concat(oldFiles)
        objectPath.set(data, prop, value)
      } else if (systemType === 'tableWaitUpsert') {
        let value = objectPath.get(formData, `@isWaitUpsert.${systemKey}`)
        objectPath.set(data, prop, value)
      } else if (systemType === 'tableConfirmUpsert') {
        let value = objectPath.get(formData, `@isConfirmUpsert.${systemKey}`)
        objectPath.set(data, prop, value)
      } else if (systemType === 'apiResponse') {
        let value = objectPath.get(formData, `@response.${systemKey}.data`)
        objectPath.set(data, prop, value)
      } else {
        let systemData = {}
        system.forEach(
          ({
            field,
            systemKey,
            systemName,
            isSearch,
            isDynamic,
            dynamicName,
          }) => {
            let value = null
            if (isSearch) {
              let componentData = objectPath.get(searchData, fullPropKey)
              value =
                objectPath.get(componentData, systemKey) ||
                objectPath.get(componentData, systemName)
            } else {
              let componentData = objectPath.get(formData, fullPropKey)
              value =
                objectPath.get(componentData, systemKey) ||
                objectPath.get(componentData, systemName)
            }
            if (systemType === 'input' || systemType === 'system') {
              if (isDynamic) {
                value = objectPath.get(formData, dynamicName)
              }
            } else {
              if (isDynamic) {
                value = objectPath.get(formData, field)
              }
            }
            objectPath.set(systemData, field, value)
          },
        )
        objectPath.set(data, `${prop}.${propKey}`, systemData)
      }
    },
  )
  objectPath.set(designerPropsData, 'data', data)
  return designerPropsData
}

export const getFormDesign = async (formCode, isReturnFormDesign = true) => {
  let formDesign = await requestFormDesign(formCode).catch(() => {})
  if (isReturnFormDesign) {
    return formDesign
  }
  let jsonForm = objectPath.get(formDesign, 'jsonData')
  return { jsonForm, formDesign }
}

export const getDesign = async (
  formCode,
  {
    pathParams,
    queryParams,
    location,
    designerData,
    datasourceValue,
    uischema,
    isPage,
  },
) => {
  if (!formCode) return
  let urlParams = { ...pathParams, ...queryParams }
  let perspective = urlParams[0] || urlParams.perspective
  let jsonForm = {}
  let searchData = {}
  let formDesign = {
    formModuleId: null,
    formDesignId: null,
    formRevisionId: null,
  }
  if (uischema) {
    let { ui } = uischema
    formDesign = ui.formDesign
    jsonForm = ui.jsonForm
  } else {
    formDesign = await getFormDesign(formCode)
    jsonForm = objectPath.get(formDesign, 'jsonData')
  }

  let formData = { perspective }

  let formGroup = objectPath.get(jsonForm, 'formGroup') || []
  if (uischema?.ui) {
    let { receiveProps = [], systemData = {} } = uischema.ui
    formData = loadDesignerData(
      receiveProps,
      { jsonForm, designerData, formData, datasourceValue, systemData },
      formGroup,
    )
  }
  let routerHistory = objectPath.get(
    jsonForm,
    'designerPropsData.routerHistory',
  )
  formData = loadRouterHistory(routerHistory, location, formData, jsonForm)
  formData = loadPropToFormData(jsonForm, formData, location)
  formData = loadUrlParamToFormData(
    jsonForm,
    formData,
    pathParams,
    queryParams,
    datasourceValue,
  )
  formData = await loadApiToFormData(jsonForm, formData, {
    urlParams,
    designerData,
    datasourceValue,
  })
  let designerPropsData = objectPath.get(jsonForm, 'designerPropsData')
  designerPropsData = createDesignerData(designerPropsData, {}, formData)

  if (isPage) {
    jsonForm = {
      ...jsonForm,
      uischema: objectPath.get(jsonForm, 'uischema') || pageUischema,
      schema: objectPath.get(jsonForm, 'schema') || pageSchema,
    }
    let field = objectPath.get(jsonForm, 'search.field') || []
    field.forEach((item) => {
      if (formData[item.key]) {
        objectPath.set(searchData, item.key, formData[item.key])
      }
    })
  } else {
    jsonForm = {
      ...jsonForm,
      uischema: objectPath.get(jsonForm, 'uischema') || formUischema,
      schema: objectPath.get(jsonForm, 'schema') || formSchema,
    }
  }
  jsonForm = {
    ...jsonForm,
    formData: formData,
    formModuleId: objectPath.get(formDesign, 'formModuleId') || null,
    formDesignId: objectPath.get(formDesign, 'formDesignId') || null,
    formRevisionId: objectPath.get(formDesign, 'formRevisionId') || null,
    updatedAt: objectPath.get(formDesign, 'updatedAt'),
    designerPropsData: designerPropsData,
    componentPropsData: {},
  }
  objectPath.del(formDesign, 'jsonData')
  return {
    jsonForm: jsonForm,
    formDesign: formDesign,
    searchData: searchData,
    datasource: datasourceValue,
  }
}

export const getForm = async (
  formCode,
  pathParams,
  queryParams,
  location,
  useFormData = false,
  designerData = {},
  datasourceValue,
) => {
  let urlParams = { ...pathParams, ...queryParams }
  let perspective = urlParams[0]
  let jsonForm = {}
  let formDesign = {
    formModuleId: null,
    formDesignId: null,
    formRevisionId: null,
  }
  if (formCode) {
    formDesign = await requestFormDesign(formCode)
    jsonForm = objectPath.get(formDesign, 'jsonData')
    let formData = { perspective }
    if (useFormData) {
      let routerHistory = objectPath.get(
        jsonForm,
        'designerPropsData.routerHistory',
      )
      formData = loadRouterHistory(routerHistory, location, formData, jsonForm)
      formData = loadPropToFormData(jsonForm, formData, location)
      formData = loadUrlParamToFormData(
        jsonForm,
        formData,
        pathParams,
        queryParams,
        datasourceValue,
      )
      formData = await loadApiToFormData(jsonForm, formData, {
        urlParams,
        designerData,
        datasourceValue,
      })
    }
    let designerPropsData = objectPath.get(jsonForm, 'designerPropsData')
    designerPropsData = createDesignerData(designerPropsData, {}, formData)

    jsonForm = {
      ...jsonForm,
      formData: formData,
      uischema: objectPath.get(jsonForm, 'uischema') || formUischema,
      schema: objectPath.get(jsonForm, 'schema') || formSchema,
      formModuleId: objectPath.get(formDesign, 'formModuleId') || null,
      formDesignId: objectPath.get(formDesign, 'formDesignId') || null,
      formRevisionId: objectPath.get(formDesign, 'formRevisionId') || null,
      updatedAt: objectPath.get(formDesign, 'updatedAt'),
      designerPropsData: designerPropsData,
      componentPropsData: {},
    }
    objectPath.del(formDesign, 'jsonData')
  }

  return {
    jsonForm: jsonForm,
    formDesign: formDesign,
    datasource: datasourceValue,
  }
}

export const getPage = async (
  pageCode,
  pathParams,
  queryParams,
  location,
  useFormData = false,
  designerData = {},
  datasourceValue,
) => {
  let urlParams = { ...pathParams, ...queryParams }
  let perspective = urlParams[0]
  let jsonForm = {}
  let searchData = {}
  let formDesign = {
    formModuleId: null,
    formDesignId: null,
    formRevisionId: null,
  }
  if (pageCode) {
    formDesign = await getFormDesign(pageCode)
    jsonForm = objectPath.get(formDesign, 'jsonData')
    let formData = { perspective }
    if (useFormData) {
      let routerHistory = objectPath.get(
        jsonForm,
        'designerPropsData.routerHistory',
      )
      formData = loadRouterHistory(routerHistory, location, formData, jsonForm)
      formData = loadPropToFormData(jsonForm, formData, location)
      formData = loadUrlParamToFormData(
        jsonForm,
        formData,
        pathParams,
        queryParams,
        datasourceValue,
      )
    }

    let designerPropsData = objectPath.get(jsonForm, 'designerPropsData')
    designerPropsData = createDesignerData(
      designerPropsData,
      searchData,
      formData,
    )

    jsonForm = {
      ...jsonForm,
      formData: formData,
      uischema: objectPath.get(jsonForm, 'uischema') || pageUischema,
      schema: objectPath.get(jsonForm, 'schema') || pageSchema,
      formModuleId: objectPath.get(formDesign, 'formModuleId') || null,
      formDesignId: objectPath.get(formDesign, 'formDesignId') || null,
      formRevisionId: objectPath.get(formDesign, 'formRevisionId') || null,
      updatedAt: objectPath.get(formDesign, 'updatedAt'),
      designerPropsData: designerPropsData,
      componentPropsData: {},
    }
    let field = objectPath.get(jsonForm, 'search.field') || []
    field.forEach((item) => {
      if (formData[item.key]) {
        objectPath.set(searchData, item.key, formData[item.key])
      }
    })
    objectPath.del(formDesign, 'jsonData')
  }
  return {
    jsonForm: jsonForm,
    formDesign: formDesign,
    searchData: searchData,
    datasource: datasourceValue,
  }
}

export const getWidgetDesign = async ({
  widgetCode,
  formRevisionId = null,
}) => {
  let jsonForm = {}
  if (!widgetCode) return jsonForm
  let mainDesign = await requestFormDesign(widgetCode)
  if (mainDesign) {
    let { jsonData } = mainDesign
    jsonForm = jsonData
    if (formRevisionId) {
      let apiDesign = `/designer/form-revision/${formRevisionId}`
      let revisionDesign = await httpClient
        .get(apiDesign)
        .then(({ data }) => data?.data)
        .catch(() => {})
      if (revisionDesign) {
        let { jsonData } = revisionDesign
        jsonForm = {
          ...jsonForm,
          uischema: jsonData.uischema || widgetUischema,
          schema: jsonData.schema || widgetSchema,
          formRevisionId: formRevisionId,
          updatedAt: revisionDesign.createdAt,
        }
      }
    }
  }

  return jsonForm
}

export const getWidget = async ({
  widgetCode,
  pathParams = {},
  queryParams = {},
  location = {},
  useFormData = false,
  formRevisionId = null,
  oldFormDesign = {},
  isRaw = false,
  designerData = {},
  datasourceValue,
}) => {
  let jsonForm = {}
  let formDesign = {
    formModuleId: null,
    formDesignId: null,
    formRevisionId: null,
  }
  let urlParams = { ...pathParams, ...queryParams }

  if (!widgetCode) {
    return {
      jsonForm: jsonForm,
      formDesign: formDesign,
      datasource: datasourceValue,
    }
  }

  let oldJsonForm = await getFormDesign(widgetCode)
  if (formRevisionId) {
    let apiDesign = `/designer/form-revision/${formRevisionId}`
    formDesign = await httpClient
      .get(apiDesign)
      .then(({ data }) => data?.data)
      .catch(() => {})
    if (isRaw) {
      return formDesign
    }
    if (formDesign) {
      let jsonData = objectPath.get(formDesign, 'jsonData')
      jsonForm = {
        ...oldJsonForm,
        uischema: objectPath.get(jsonData, 'uischema') || widgetUischema,
        schema: objectPath.get(jsonData, 'schema') || widgetSchema,
        formRevisionId: formRevisionId,
        updatedAt: objectPath.get(formDesign, 'createdAt'),
      }
      formDesign = { ...oldFormDesign, ...formDesign }
      objectPath.del(formDesign, 'jsonData')
    }
  } else {
    formDesign = oldJsonForm
    if (isRaw) {
      return formDesign
    }
    if (formDesign) {
      jsonForm = objectPath.get(formDesign, 'jsonData')
      let formData = {}
      if (useFormData) {
        let routerHistory = objectPath.get(
          jsonForm,
          'designerPropsData.routerHistory',
        )
        formData = loadRouterHistory(
          routerHistory,
          location,
          formData,
          jsonForm,
        )
        formData = loadPropToFormData(jsonForm, formData, location)
        formData = await loadApiToFormData(jsonForm, formData, {
          urlParams,
          designerData,
          datasourceValue,
        })
        formData = loadUrlParamToFormData(
          jsonForm,
          formData,
          pathParams,
          queryParams,
          datasourceValue,
        )
      }
      let designerPropsData = objectPath.get(jsonForm, 'designerPropsData')
      designerPropsData = createDesignerData(designerPropsData, {}, formData)
      jsonForm = {
        ...jsonForm,
        formData: formData,
        uischema: objectPath.get(jsonForm, 'uischema') || widgetUischema,
        schema: objectPath.get(jsonForm, 'schema') || widgetSchema,
        formModuleId: objectPath.get(formDesign, 'formModuleId') || null,
        formDesignId: objectPath.get(formDesign, 'formDesignId') || null,
        formRevisionId: objectPath.get(formDesign, 'formRevisionId') || null,
        updatedAt: objectPath.get(formDesign, 'updatedAt'),
        designerPropsData: designerPropsData,
        componentPropsData: {},
      }
      objectPath.del(formDesign, 'jsonData')
    }
  }
  return {
    jsonForm: jsonForm,
    formDesign: formDesign,
    datasource: datasourceValue,
  }
}

export const getDashboard = async ({
  dashboardCode,
  pathParams = {},
  queryParams = {},
  location = {},
  useFormData = false,
  designerData = {},
}) => {
  let jsonForm = {}
  let formDesign = {
    formModuleId: null,
    formDesignId: null,
    formRevisionId: null,
  }
  let api = ''
  let urlParams = { ...pathParams, ...queryParams }
  api = `/designer/dashboard-layout/getByCode?code=${dashboardCode}`
  formDesign = await httpClient
    .get(api)
    .then(({ data }) => data?.data)
    .catch(() => {})

  if (formDesign) {
    jsonForm = objectPath.get(formDesign, 'jsonData')
    let formData = {}
    if (useFormData) {
      formData = loadUrlParamToFormData(
        jsonForm,
        formData,
        pathParams,
        queryParams,
      )

      let routerHistory = objectPath.get(
        jsonForm,
        'designerPropsData.routerHistory',
      )
      formData = loadRouterHistory(routerHistory, location, formData, jsonForm)
      formData = loadPropToFormData(jsonForm, formData, location)
      formData = await loadApiToFormData(jsonForm, formData, {
        urlParams,
        designerData,
      })
    }
    jsonForm = {
      ...jsonForm,
      formData: formData,
      uischema: objectPath.get(jsonForm, 'uischema') || dashboardUischema,
      schema: objectPath.get(jsonForm, 'schema') || dashboardSchema,
      formModuleId: objectPath.get(formDesign, 'formModuleId') || null,
      formDesignId: objectPath.get(formDesign, 'formDesignId') || null,
      formRevisionId: objectPath.get(formDesign, 'formRevisionId') || null,
      updatedAt: objectPath.get(formDesign, 'updatedAt'),
    }
    objectPath.del(formDesign, 'jsonData')
  }

  return { jsonForm: jsonForm, formDesign: formDesign }
}

const createWidgetDesign = async (jsonForm) => {
  let mapTag = jsonForm?.schema?.mapTag
  let widgetSchema = Object.keys(mapTag).filter(
    (key) => mapTag[key] === 'widget',
  )
  let widgetDesign = {}
  let allWidgetCode = []

  widgetSchema.forEach((key) => {
    let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
    let widgetCode = ui?.widget?.formDesign?.code
    let formRevisionId = ui?.widget?.formDesign?.formRevisionId
    let hasWidget = allWidgetCode.find((item) => {
      return (
        widgetCode === item.widgetCode && formRevisionId === item.formRevisionId
      )
    })
    if (!hasWidget) {
      allWidgetCode.push({ widgetCode, formRevisionId })
    }
  })

  allWidgetCode.forEach(async ({ widgetCode, formRevisionId }) => {
    new Promise(async (resolve) => {
      let design = await getWidgetDesign({
        widgetCode,
        formRevisionId,
      })
      let mapFormData = design?.schema?.mapFormData
      if (mapFormData) {
        Object.keys(mapFormData).forEach((key) => {
          let ui = getCurrentUi(key, design.schema, design.uischema)
          objectPath.push(design, ['widgetField'], {
            name: ui.name,
            fieldName: ui.fieldName,
          })
        })
      }
      resolve(design)
    }).then((response) => {
      objectPath.set(widgetDesign, widgetCode, response)
    })
  })
  return widgetDesign
}

export const resetJsonForm = (props) => {
  let {
    designCode,
    formDesign,
    jsonForm,
    perspectiveRoute,
    resetContext,
    uischema,
    schema,
    isSearch = false,
    searchData = {},
    isPage = 0,
    designerProps = null,
    designerUi = [],
    elementAction,
    showInvisible = false,
    callback = {},
    isForm = 0,
    datasource = {},
    urlParams,
    designerDatasource = null,
    detailPanelProps,
    user,
    formatDate,
    formatTime,
    formatDateTime,
    dateFormat,
    timeFormat,
    isWidget,
    widgetName,
    employeeList,
    isOpenModal,
    datasourceValue = {},
    auth = {},
    isImmediateReset = false,
  } = props

  let resetData = {}
  const configFormCode = [
    designCode,
    formDesign?.code,
    objectPath.get(jsonForm, 'formCode'),
  ].filter(Boolean)
  objectPath.set(jsonForm, 'configFormCode', configFormCode)
  jsonForm = setDefaultValueToFormData(jsonForm, auth)
  objectPath.set(jsonForm, 'accountConfig', auth?.config || [])
  jsonForm = applyRequireFieldConfig(jsonForm, auth?.config || [])
  elementAction = applyDefaultRadioActions(jsonForm, elementAction || {})
  if (designCode && formDesign) {
    objectPath.del(formDesign, 'jsonData')
    resetData = {
      designCode: designCode,
      formDesign: formDesign,
      jsonForm: jsonForm,
      current: {
        ui: objectPath.get(jsonForm, 'uischema') || {},
        schema: objectPath.get(jsonForm, 'schema') || {},
        readOnly: false,
      },
      perspectiveRoute: perspectiveRoute,
      isPage: isPage,
      isForm: isForm,
      isModal: isOpenModal,
      designerProps: designerProps,
      designerUi: designerUi,
      elementAction: elementAction,
      showInvisible: showInvisible,
      callback: callback,
      [`@datasource`]: datasource,
      designerDatasource: designerDatasource,
      urlParams: urlParams,
      detailPanelProps: detailPanelProps,
      user: user,
      formatDate: formatDate,
      formatTime: formatTime,
      formatDateTime: formatDateTime,
      dateFormat: dateFormat,
      timeFormat: timeFormat,
      // widgetDesign: widgetDesign,
      widgetName: widgetName,
      datasourceValue,
    }
    if (employeeList) {
      resetData.employeeList = employeeList
    }
  } else {
    resetData = {
      formDesign: {
        formModuleId: null,
        formDesignId: null,
        formRevisionId: null,
      },
      jsonForm: {
        ...jsonForm,
        formData: {},
        uischema: uischema,
        schema: schema,
      },
      current: {
        ui: uischema,
        schema: schema,
        readOnly: false,
      },
      perspectiveRoute: perspectiveRoute,
      isPage: isPage,
      isForm: isForm,
      isReset: 1,
      elementAction: {},
      [`@datasource`]: {},
      designerDatasource: null,
      urlParams: urlParams,
      user: user,
    }
  }
  if (isSearch) {
    resetData = {
      ...resetData,
      searchData: searchData,
      searchResult: [],
    }
  }
  if (
    urlParams?.formCode === designCode ||
    urlParams?.pageCode === designCode
  ) {
    objectPath.set(resetData, 'isUiChanged', {})
  }
  if (isImmediateReset) {
    resetContext(resetData)
  } else {
    debounceCallback(resetContext, [resetData], widgetName ?? designCode)
  }
}

export const checkDateText = ({
  dateList,
  formData,
  jsonForm,
  formatDateTime,
  widgetData,
}) => {
  dateList.forEach((dateUi) => {
    let { dateText } = dateUi
    if (!dateText) return
    let dateKey = dateUi.key
    let dateName = dateUi.name
    let { formatSort, timeFormat, textField } = dateText
    let textKey = textField.key
    if (!textKey) return
    let textUi = getCurrentUi(textKey, jsonForm.schema, jsonForm.uischema)
    let textName = textUi.name
    let dateValue =
      objectPath.get(widgetData, dateName) || objectPath.get(formData, dateKey)
    let textValue
    if (dateValue) {
      let dateString = dayjs(dateValue).format('YYYY-MM-DD')
      let dateValueList = []
      if (formatSort) {
        formatSort.forEach((format) => {
          let formatValue = objectPath.get(dateText, format)
          let value = formatDateTime(dateString, formatValue)
          dateValueList.push(value)
        })
      }
      if (timeFormat) {
        let value = formatDateTime(dateString, timeFormat)
        dateValueList.push(value)
      }
      textValue = dateValueList.join(' ')
    } else {
      textValue = ''
    }
    objectPath.set(formData, textKey, textValue)
    objectPath.set(formData, textName, textValue)
  })
  return formData
}

export const checkFieldConnect = (props) => {
  let { jsonForm, prop = {} } = props
  let widgetData =
    prop?.formData && prop?.widgetName
      ? objectPath.get(prop.formData, prop.widgetName)
      : {}
  let formData = cloneDeep(jsonForm.formData)
  let { mapTag } = jsonForm.schema
  let dateList = []
  Object.keys(mapTag).forEach((key) => {
    let tag = mapTag[key]
    let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
    if (ui.key !== key) return
    if (tag === 'date') {
      dateList.push(ui)
    }
  })
  formData = checkDateText({ ...props, dateList, formData, widgetData })
  jsonForm.formData = formData
  return jsonForm
}

const setSearchParam = (destination, dataUi, designContext) => {
  let { jsonForm, setJsonForm } = designContext
  if (destination === 'header') {
    let searchField = objectPath.get(jsonForm, 'search.field') || []
    let param = {
      formDesignId: jsonForm.formDesignId,
      collection: '',
      collectionName: '',
      key: dataUi.key,
      field: dataUi.fieldName,
      name: dataUi.name,
    }
    searchField.push(param)
    let newJsonForm = jsonForm
    objectPath.set(jsonForm, 'search.field', searchField)
    setJsonForm(newJsonForm)
  }
}

export const updateMapWidget = (
  widgetJsonForm,
  newKey,
  designContext,
  headKey,
) => {
  let { jsonForm, setSchema, setJsonForm } = designContext
  if (widgetJsonForm) {
    let widgetMapFormData =
      objectPath.get(widgetJsonForm, 'schema.mapFormData') || {}
    let widgetMapName = objectPath.get(widgetJsonForm, 'schema.mapName') || {}
    let widgetMapKey = objectPath.get(widgetJsonForm, 'schema.mapKey') || {}
    let widgetMapField = objectPath.get(widgetJsonForm, 'schema.mapField') || {}
    let widgetMapTag = objectPath.get(widgetJsonForm, 'schema.mapTag') || {}

    let filterDynamic = Object.keys(widgetMapTag).filter(
      (key) => widgetMapTag[key] === 'dynamic',
    )
    let dynamicList = []

    filterDynamic.forEach((key) => dynamicList.push(widgetMapKey[key]))
    let filterWidgetFormData = Object.keys(widgetMapKey).filter((key) => {
      let str = widgetMapKey[key] ?? ''
      if (widgetMapTag[key] === 'dynamic') {
        return true
      }
      if (widgetMapFormData[key]) {
        let isDynamic = filterDynamic.find((dynamicKey) => {
          return str.search(dynamicKey) > -1
        })
        if (isDynamic) {
          return false
        }
        return true
      }

      return false
    })
    let schema = jsonForm.schema
    let headName = objectPath.get(jsonForm, `schema.mapName.${headKey}`)
    objectPath.set(schema, 'mapWidget', {})
    filterWidgetFormData.forEach((key) => {
      let name = widgetMapName[key].split('.').pop()
      objectPath.set(schema, `mapWidget.name.${key}`, `${headName}.${name}`)
      objectPath.set(schema, `mapWidget.field.${key}`, widgetMapField[key])
      objectPath.set(
        schema,
        `mapWidget.dynamic.${key}`,
        widgetMapTag[key] === 'dynamic',
      )
      let ui = getCurrentUi(key, widgetJsonForm.schema, widgetJsonForm.uischema)
      objectPath.set(schema, `mapWidget.ui.${key}`, ui)
    })
    if (typeof setSchema === 'function') {
      setSchema(schema)
    } else if (typeof setJsonForm === 'function') {
      objectPath.set(jsonForm, 'schema', schema)
      setJsonForm(jsonForm)
    }
  }
}

const getWidgetJsonData = async (
  widgetCode,
  newKey,
  designContext,
  headKey,
) => {
  let widgetData = await getWidget({
    widgetCode: widgetCode,
  })
  let widgetJsonData = {
    formDesign: objectPath.get(widgetData, 'formDesign'),
    jsonForm: objectPath.get(widgetData, 'jsonForm'),
    schema: objectPath.get(widgetData, 'jsonForm.schema'),
    receiveProps: objectPath.get(
      widgetData,
      'jsonForm.designerPropsData.receive',
    ),
  }
  updateMapWidget(widgetData.jsonForm, newKey, designContext, headKey)
  return widgetJsonData
}

export const createDesign = async (result, designContext) => {
  let {
    subGroup,
    jsonForm,
    current,
    generateString,
    addUiForContainer,
    addUiForLayout,
    addInsideModal,
    addInsideTab,
    addInsideDynamic,
    reorderUiSchema,
    moveUiSchema,
    moveSchema,
    getDataUi,
    updateUiSchema,
    setDesignerPropsData,
  } = designContext
  let { schema } = jsonForm
  const { destination, draggableId, source } = result
  if (
    !destination ||
    destination.droppableId === 'element' ||
    destination.droppableId === 'container' ||
    destination.droppableId === 'widget' ||
    destination.droppableId === 'component'
  )
    return

  if (
    destination.droppableId === 'header' ||
    destination.droppableId === 'body' ||
    destination.droppableId === 'footer'
  ) {
    let layoutKey = destination.droppableId
    if (source.droppableId === 'container') {
      let found = staticContainer.find(({ id }) => id === draggableId)
      if (draggableId === 'modal') {
        // add modal wrapper
        let modal = staticContainer.find(({ id }) => id === 'modal')
        let modalLayout = getDataUi(layoutKey, result, modal)
        let modalKey = addUiForLayout(result, modalLayout)
        let mapKey = schema.mapKey[modalKey]

        // add modal body
        let modalBody = modal.uischema.part.modalBody
        modalBody.uischema.layout = layoutKey
        let modalBodyKey = 'modalBody' + generateString(5)
        addInsideModal(modalBody, mapKey, modalKey, modalBodyKey)

        // add modal footer
        let modalFooter = modal.uischema.part.modalFooter
        modalFooter.uischema.layout = layoutKey
        let modalFooterKey = 'modalFooter' + generateString(5)
        addInsideModal(modalFooter, mapKey, modalKey, modalFooterKey)

        // add button open modal
        let button = systemElement.find(({ id }) => id === 'button')
        let buttonLayout = getDataUi(layoutKey, result, {
          ...button,
          uischema: {
            ...button.uischema,
            buttonText: 'Open Modal',
            action: {
              event: 'onClick',
              name: 'openModal',
              modalType: 'internal',
              modal: {
                key: modalKey,
                data: [],
              },
              module: null,
              formId: null,
              formCode: null,
              formName: null,
            },
            modal: modalKey,
          },
        })
        addUiForLayout(result, buttonLayout)
      } else if (draggableId === 'tab') {
        // add tab
        let tab = staticContainer.find(({ id }) => id === 'tab')
        let dataLayout = getDataUi(layoutKey, result, tab)
        let wrapperKey = addUiForLayout(result, dataLayout)

        // add tab panel
        let tabPanel = tab.uischema.part.tabPanel
        tabPanel.uischema.layout = layoutKey
        let mapKey = schema.mapKey[wrapperKey]
        let tabKey = 'tabPanel' + generateString(5)
        addInsideTab(tabPanel, mapKey, wrapperKey, tabKey)
      } else if (draggableId === 'panelGroup') {
        // add panel group
        let panelGroup = staticContainer.find(({ id }) => id === 'panelGroup')
        let dataLayout = getDataUi(layoutKey, result, panelGroup)
        let wrapperKey = addUiForLayout(result, dataLayout)

        // add panel
        let panel = panelGroup.uischema.part.panel
        panel.uischema.layout = layoutKey
        let mapKey = schema.mapKey[wrapperKey]
        let tabKey = 'panel' + generateString(5)
        addInsideTab(panel, mapKey, wrapperKey, tabKey)
      } else if (draggableId === 'dynamic') {
        // add dynamic
        let dynamic = staticContainer.find(({ id }) => id === 'dynamic')
        let dataLayout = getDataUi(layoutKey, result, dynamic)
        let wrapperKey = addUiForLayout(result, dataLayout)

        // add dynamic panel
        let dynamicMapKey = schema.mapKey[wrapperKey]
        let dynamicPanel = dynamic.uischema.part.dynamicPanel
        dynamicPanel.uischema.layout = layoutKey
        let dynamicPanelKey = 'dynamicPanel' + generateString(5)
        addInsideDynamic(
          dynamicPanel,
          dynamicMapKey,
          wrapperKey,
          dynamicPanelKey,
        )
        // add edit panel
        let editPanel = dynamic.uischema.part.editPanel
        editPanel.uischema.layout = layoutKey
        let editPanelKey = 'editPanel' + generateString(5)
        addInsideDynamic(editPanel, dynamicMapKey, wrapperKey, editPanelKey)

        let newSchema = jsonForm.schema
        newSchema.mapDynamic = {
          ...newSchema.mapDynamic,
          [wrapperKey]: true,
        }
      } else {
        let dataLayout = getDataUi(layoutKey, result, found)
        addUiForLayout(result, dataLayout)
      }
    } else if (source.droppableId === 'element') {
      let staticElement = basicElement.concat(systemElement)
      let found = staticElement.find(({ id }) => id === draggableId)

      if (draggableId === 'checkboxGroup') {
        let dataLayout = getDataUi(layoutKey, result, found)
        let elementKey = addUiForLayout(result, dataLayout)
        let newSchema = jsonForm.schema
        let newKey = 'checkbox' + generateString(5)
        updateUiSchema(elementKey, 'options', [
          {
            key: newKey,
            name: newKey,
            label: 'checkbox 1',
            valueChecked: true,
            valueUnchecked: false,
            defaultChecked: false,
            formData: true,
          },
        ])
        newSchema.mapKey = {
          ...newSchema.mapKey,
          [newKey]: [layoutKey, elementKey, newKey].join('.'),
        }
        newSchema.mapName = {
          ...newSchema.mapName,
          [newKey]: [layoutKey, elementKey, newKey].join('.'),
        }
        newSchema.mapTag = {
          ...newSchema.mapTag,
          [newKey]: 'checkbox',
        }
        newSchema.mapFormData = {
          ...newSchema.mapFormData,
          [newKey]: true,
        }
      } else if (draggableId === 'checkboxInput') {
        let newKey = 'input' + generateString(5)
        objectPath.set(found, 'uischema.input', {
          key: newKey,
          name: newKey,
          field: 'input',
          value: '',
          defaultValue: '',
          formData: true,
        })
        objectPath.set(found, 'uischema.inputKey', newKey)
        let dataLayout = getDataUi(layoutKey, result, found)
        let elementKey = addUiForLayout(result, dataLayout)
        let newSchema = jsonForm.schema
        newSchema.mapKey = {
          ...newSchema.mapKey,
          [newKey]: [layoutKey, elementKey, newKey].join('.'),
        }
        newSchema.mapName = {
          ...newSchema.mapName,
          [newKey]: [layoutKey, elementKey, newKey].join('.'),
        }
        newSchema.mapTag = {
          ...newSchema.mapTag,
          [newKey]: 'input',
        }
        newSchema.mapFormData = {
          ...newSchema.mapFormData,
          [newKey]: true,
        }
      } else {
        let dataLayout = getDataUi(layoutKey, result, found)
        addUiForLayout(result, dataLayout)
      }
    } else if (source.droppableId === 'widget') {
      let dataLayout = getDataUi(layoutKey, result, staticWidget)
      let newKey = objectPath.get(dataLayout, 'dataUi.key')
      let name = draggableId
        .split('-')
        .map((item, index) => (index === 0 ? item : makeUppercaseFirst(item)))
        .join('')
      let fieldName = draggableId
        .split('-')
        .map((item) => makeUppercaseFirst(item))
        .join(' ')
      objectPath.set(dataLayout, 'dataUi.path', draggableId)
      objectPath.set(dataLayout, 'dataUi.name', name)
      objectPath.set(dataLayout, 'dataUi.fieldName', fieldName)
      let widgetCode = draggableId
      let widgetJsonData = await getWidgetJsonData(
        widgetCode,
        newKey,
        designContext,
        destination.droppableId,
      )
      objectPath.set(dataLayout, 'dataUi.widget', widgetJsonData)
      // set ui
      addUiForLayout(result, dataLayout)
      // set designer prop
      let designerPropsData = jsonForm?.designerPropsData || {}
      objectPath.set(designerPropsData, `widgetCode.${newKey}`, widgetCode)
      setDesignerPropsData(designerPropsData)
    } else if (source.droppableId === 'component') {
      let dataLayout = getDataUi(layoutKey, result, staticComponent)
      objectPath.set(dataLayout, 'dataUi.path', draggableId)
      addUiForLayout(result, dataLayout)
    } else {
      if (destination.droppableId === source.droppableId) {
        reorderUiSchema(result)
      } else {
        moveSchema(result)
        moveUiSchema(result)
      }
    }
  } else {
    let layoutKey = getDestinationLayoutKey(jsonForm, current, destination)
    if (source.droppableId === 'container') {
      let found = staticContainer.find(({ id }) => id === draggableId)
      if (draggableId === 'modal') {
        // add modal wrapper
        let modal = staticContainer.find(({ id }) => id === 'modal')
        let modalLayout = getDataUi(layoutKey, result, modal)
        let modalKey = addUiForContainer(result, modalLayout)
        let mapKey = schema.mapKey[modalKey]

        // add modal body
        let modalBody = modal.uischema.part.modalBody
        modalBody.uischema.layout = layoutKey
        let modalBodyKey = 'modalBody' + generateString(5)
        addInsideModal(modalBody, mapKey, modalKey, modalBodyKey)

        // add modal footer
        let modalFooter = modal.uischema.part.modalFooter
        modalFooter.uischema.layout = layoutKey
        let modalFooterKey = 'modalFooter' + generateString(5)
        addInsideModal(modalFooter, mapKey, modalKey, modalFooterKey)

        // add button open modal
        let button = systemElement.find(({ id }) => id === 'button')
        let buttonLayout = getDataUi(layoutKey, result, {
          ...button,
          uischema: {
            ...button.uischema,
            buttonText: 'Open Modal',
            action: {
              event: 'onClick',
              name: 'openModal',
              modalType: 'internal',
              modal: {
                key: modalKey,
                data: [],
              },
              module: null,
              formId: null,
              formCode: null,
              formName: null,
            },
            modal: modalKey,
          },
        })
        addUiForContainer(result, buttonLayout)
      } else if (draggableId === 'tab') {
        // add tab
        let tab = staticContainer.find(({ id }) => id === 'tab')
        let dataLayout = getDataUi(layoutKey, result, tab, !subGroup)
        let wrapperKey = addUiForContainer(result, dataLayout)

        // add tab panel
        let tabPanel = tab.uischema.part.tabPanel
        tabPanel.uischema.layout = layoutKey
        let mapKey = schema.mapKey[wrapperKey]
        let tabKey = 'tabPanel' + generateString(5)
        addInsideTab(tabPanel, mapKey, wrapperKey, tabKey)
      } else if (draggableId === 'panelGroup') {
        // add panel group
        let panelGroup = staticContainer.find(({ id }) => id === 'panelGroup')
        let dataLayout = getDataUi(layoutKey, result, panelGroup, !subGroup)
        let wrapperKey = addUiForContainer(result, dataLayout)

        // add panel
        let panel = panelGroup.uischema.part.panel
        panel.uischema.layout = layoutKey
        let mapKey = schema.mapKey[wrapperKey]
        let tabKey = 'panel' + generateString(5)
        addInsideTab(panel, mapKey, wrapperKey, tabKey)
      } else if (draggableId === 'dynamic') {
        // add dynamic
        let dynamic = staticContainer.find(({ id }) => id === 'dynamic')
        let dataLayout = getDataUi(layoutKey, result, dynamic, !subGroup)
        let wrapperKey = addUiForContainer(result, dataLayout)
        // add dynamic panel
        let dynamicMapKey = schema.mapKey[wrapperKey]
        let dynamicPanel = dynamic.uischema.part.dynamicPanel
        dynamicPanel.uischema.layout = layoutKey
        let dynamicPanelKey = 'dynamicPanel' + generateString(5)
        addInsideDynamic(
          dynamicPanel,
          dynamicMapKey,
          wrapperKey,
          dynamicPanelKey,
        )
        // add edit panel
        let editPanel = dynamic.uischema.part.editPanel
        editPanel.uischema.layout = layoutKey
        let editPanelKey = 'editPanel' + generateString(5)
        addInsideDynamic(editPanel, dynamicMapKey, wrapperKey, editPanelKey)

        let newSchema = jsonForm.schema
        newSchema.mapDynamic = {
          ...newSchema.mapDynamic,
          [wrapperKey]: true,
        }
      } else {
        let dataLayout = getDataUi(layoutKey, result, found, !subGroup)
        addUiForContainer(result, dataLayout)
      }
    } else if (source.droppableId === 'widget') {
      let dataLayout = getDataUi(layoutKey, result, staticWidget)
      let newKey = objectPath.get(dataLayout, 'dataUi.key')
      let name = draggableId
        .split('-')
        .map((item, index) => (index === 0 ? item : makeUppercaseFirst(item)))
        .join('')
      let fieldName = draggableId
        .split('-')
        .map((item) => makeUppercaseFirst(item))
        .join(' ')
      objectPath.set(dataLayout, 'dataUi.path', draggableId)
      objectPath.set(dataLayout, 'dataUi.name', name)
      objectPath.set(dataLayout, 'dataUi.fieldName', fieldName)
      let widgetCode = draggableId
      let widgetJsonData = await getWidgetJsonData(
        widgetCode,
        newKey,
        designContext,
        destination.droppableId,
      )
      objectPath.set(dataLayout, 'dataUi.widget', widgetJsonData)
      addUiForContainer(result, dataLayout)
      let designerPropsData = jsonForm?.designerPropsData || {}
      objectPath.set(designerPropsData, `widgetCode.${newKey}`, draggableId)
      setDesignerPropsData(designerPropsData)
    } else if (source.droppableId === 'element') {
      let staticElement = basicElement.concat(systemElement)
      let found = staticElement.find(({ id }) => id === draggableId)
      if (draggableId === 'checkboxGroup') {
        let dataLayout = getDataUi(layoutKey, result, found)
        let elementKey = addUiForContainer(result, dataLayout)
        let editorKey = jsonForm.schema.mapKey[destination.droppableId]
        let mapName = jsonForm.schema.mapName[destination.droppableId]
        let newKey = 'checkbox' + generateString(5)
        updateUiSchema(elementKey, 'options', [
          {
            key: newKey,
            name: newKey,
            label: 'checkbox 1',
            valueChecked: true,
            valueUnchecked: false,
            defaultChecked: false,
            formData: true,
          },
        ])
        let newSchema = jsonForm.schema
        newSchema.mapTag = {
          ...newSchema.mapTag,
          [newKey]: 'checkbox',
        }
        newSchema.mapFormData = {
          ...newSchema.mapFormData,
          [newKey]: true,
        }
        newSchema.mapKey = {
          ...newSchema.mapKey,
          [newKey]: [editorKey, newKey].join('.'),
        }
        newSchema.mapName = {
          ...newSchema.mapName,
          [newKey]: [mapName, newKey].join('.'),
        }
      } else if (draggableId === 'checkboxInput') {
        let newKey = 'input' + generateString(5)
        let newName = newKey
        objectPath.set(found, 'uischema.input', {
          key: newKey,
          name: newKey,
          field: 'input',
          value: '',
          defaultValue: '',
          formData: true,
        })
        objectPath.set(found, 'uischema.inputKey', newKey)
        let dataLayout = getDataUi(layoutKey, result, found)
        addUiForContainer(result, dataLayout)
        let editorKey = jsonForm.schema.mapKey[destination.droppableId]
        let mapName = jsonForm.schema.mapName[destination.droppableId]
        let newSchema = jsonForm.schema

        newSchema.mapTag = {
          ...newSchema.mapTag,
          [newKey]: 'input',
        }
        newSchema.mapFormData = {
          ...newSchema.mapFormData,
          [newKey]: true,
        }
        newSchema.mapKey = {
          ...newSchema.mapKey,
          [newKey]: [editorKey, newKey].join('.'),
        }
        newSchema.mapName = {
          ...newSchema.mapName,
          [newKey]: [mapName, newName].join('.'),
        }
      } else {
        let dataLayout = getDataUi(layoutKey, result, found)
        addUiForContainer(result, dataLayout)
      }
    } else if (source.droppableId === 'component') {
      let dataLayout = getDataUi(layoutKey, result, staticComponent)
      objectPath.set(dataLayout, 'dataUi.path', draggableId)
      addUiForContainer(result, dataLayout)
    } else {
      if (destination.droppableId === source.droppableId) {
        reorderUiSchema(result)
      } else {
        moveSchema(result)
        moveUiSchema(result)
      }
    }
  }
}

export const createDesignPage = async (result, designContext) => {
  let {
    subGroup,
    jsonForm,
    current,
    generateString,
    addUiForContainer,
    addUiForLayout,
    addInsideModal,
    addInsideTab,
    addInsideDynamic,
    reorderUiSchema,
    moveUiSchema,
    moveSchema,
    getDataUi,
    updateUiSchema,
    setDesignerPropsData,
    setJsonForm,
  } = designContext
  let { schema } = jsonForm
  const { destination, draggableId, source } = result
  if (
    !destination ||
    destination.droppableId === 'element' ||
    destination.droppableId === 'container' ||
    destination.droppableId === 'widget' ||
    destination.droppableId === 'component'
  )
    return

  if (
    destination.droppableId === 'header' ||
    destination.droppableId === 'body'
  ) {
    let layoutKey = destination.droppableId
    if (source.droppableId === 'container') {
      let found = staticContainer.find(({ id }) => id === draggableId)
      if (draggableId === 'modal') {
        // add modal wrapper
        let modal = staticContainer.find(({ id }) => id === 'modal')
        let modalLayout = getDataUi(layoutKey, result, modal)
        let modalKey = addUiForLayout(result, modalLayout)
        let mapKey = schema.mapKey[modalKey]

        // add modal body
        let modalBody = modal.uischema.part.modalBody
        modalBody.uischema.layout = layoutKey
        let modalBodyKey = 'modalBody' + generateString(5)
        addInsideModal(modalBody, mapKey, modalKey, modalBodyKey)

        // add modal footer
        let modalFooter = modal.uischema.part.modalFooter
        modalFooter.uischema.layout = layoutKey
        let modalFooterKey = 'modalFooter' + generateString(5)
        addInsideModal(modalFooter, mapKey, modalKey, modalFooterKey)

        // add button open modal
        let button = systemElement.find(({ id }) => id === 'button')
        let buttonLayout = getDataUi(layoutKey, result, {
          ...button,
          uischema: {
            ...button.uischema,
            buttonText: 'Open Modal',
            action: {
              event: 'onClick',
              name: 'openModal',
              modalType: 'internal',
              modal: {
                key: modalKey,
                data: [],
              },
              module: null,
              formId: null,
              formCode: null,
              formName: null,
            },
            modal: modalKey,
          },
        })
        addUiForLayout(result, buttonLayout)
      } else if (draggableId === 'tab') {
        // add tab
        let tab = staticContainer.find(({ id }) => id === 'tab')
        let dataLayout = getDataUi(layoutKey, result, tab)
        let wrapperKey = addUiForLayout(result, dataLayout)

        // add tab panel
        let tabPanel = tab.uischema.part.tabPanel
        tabPanel.uischema.layout = layoutKey
        let mapKey = schema.mapKey[wrapperKey]
        let tabKey = 'tabPanel' + generateString(5)
        addInsideTab(tabPanel, mapKey, wrapperKey, tabKey)
      } else if (draggableId === 'panelGroup') {
        // add panel group
        let panelGroup = staticContainer.find(({ id }) => id === 'panelGroup')
        let dataLayout = getDataUi(layoutKey, result, panelGroup)
        let wrapperKey = addUiForLayout(result, dataLayout)

        // add panel
        let panel = panelGroup.uischema.part.panel
        panel.uischema.layout = layoutKey
        let mapKey = schema.mapKey[wrapperKey]
        let tabKey = 'panel' + generateString(5)
        addInsideTab(panel, mapKey, wrapperKey, tabKey)
      } else if (draggableId === 'dynamic') {
        // add dynamic
        let dynamic = staticContainer.find(({ id }) => id === 'dynamic')
        let dataLayout = getDataUi(layoutKey, result, dynamic)
        let wrapperKey = addUiForLayout(result, dataLayout)

        // add dynamic panel
        let dynamicMapKey = schema.mapKey[wrapperKey]
        let dynamicPanel = dynamic.uischema.part.dynamicPanel
        dynamicPanel.uischema.layout = layoutKey
        let dynamicPanelKey = 'dynamicPanel' + generateString(5)
        addInsideDynamic(
          dynamicPanel,
          dynamicMapKey,
          wrapperKey,
          dynamicPanelKey,
        )
        // add edit panel
        let editPanel = dynamic.uischema.part.editPanel
        editPanel.uischema.layout = layoutKey
        let editPanelKey = 'editPanel' + generateString(5)
        addInsideDynamic(editPanel, dynamicMapKey, wrapperKey, editPanelKey)

        let newSchema = jsonForm.schema
        newSchema.mapDynamic = {
          ...newSchema.mapDynamic,
          [wrapperKey]: true,
        }
      } else {
        let dataLayout = getDataUi(layoutKey, result, found)
        addUiForLayout(result, dataLayout)
      }
    } else if (source.droppableId === 'element') {
      let staticElement = basicElement.concat(systemElement)
      let found = staticElement.find(({ id }) => id === draggableId)
      let dataLayout = getDataUi(layoutKey, result, found)
      if (draggableId === 'checkboxGroup') {
        let elementKey = addUiForLayout(result, dataLayout)
        let newSchema = jsonForm.schema
        let newKey = 'checkbox' + generateString(5)
        updateUiSchema(elementKey, 'options', [
          {
            key: newKey,
            name: newKey,
            label: 'checkbox 1',
            valueChecked: true,
            valueUnchecked: false,
            defaultChecked: false,
            formData: true,
          },
        ])
        newSchema.mapKey = {
          ...newSchema.mapKey,
          [newKey]: [layoutKey, elementKey, newKey].join('.'),
        }
        newSchema.mapName = {
          ...newSchema.mapName,
          [newKey]: [layoutKey, elementKey, newKey].join('.'),
        }
        newSchema.mapTag = {
          ...newSchema.mapTag,
          [newKey]: 'checkbox',
        }
        newSchema.mapFormData = {
          ...newSchema.mapFormData,
          [newKey]: true,
        }
      } else if (draggableId === 'checkboxInput') {
        let newKey = 'input' + generateString(5)
        objectPath.set(found, 'uischema.input', {
          key: newKey,
          name: newKey,
          field: 'input',
          value: '',
          defaultValue: '',
          formData: true,
        })
        objectPath.set(found, 'uischema.inputKey', newKey)
        let elementKey = addUiForLayout(result, dataLayout)
        let newSchema = jsonForm.schema
        newSchema.mapKey = {
          ...newSchema.mapKey,
          [newKey]: [layoutKey, elementKey, newKey].join('.'),
        }
        newSchema.mapName = {
          ...newSchema.mapName,
          [newKey]: [layoutKey, elementKey, newKey].join('.'),
        }
        newSchema.mapTag = {
          ...newSchema.mapTag,
          [newKey]: 'input',
        }
        newSchema.mapFormData = {
          ...newSchema.mapFormData,
          [newKey]: true,
        }
      } else {
        addUiForLayout(result, dataLayout)
      }
      setSearchParam(layoutKey, dataLayout.dataUi, designContext)
    } else if (source.droppableId === 'widget') {
      let dataLayout = getDataUi(layoutKey, result, staticWidget)
      let newKey = objectPath.get(dataLayout, 'dataUi.key')
      let name = draggableId
        .split('-')
        .map((item, index) => (index === 0 ? item : makeUppercaseFirst(item)))
        .join('')
      let fieldName = draggableId
        .split('-')
        .map((item) => makeUppercaseFirst(item))
        .join(' ')
      objectPath.set(dataLayout, 'dataUi.path', draggableId)
      objectPath.set(dataLayout, 'dataUi.name', name)
      objectPath.set(dataLayout, 'dataUi.fieldName', fieldName)
      let widgetCode = draggableId
      let widgetJsonData = await getWidgetJsonData(
        widgetCode,
        newKey,
        designContext,
        destination.droppableId,
      )
      objectPath.set(dataLayout, 'dataUi.widget', widgetJsonData)
      if (layoutKey === 'header') {
        objectPath.set(dataLayout, 'dataUi.isSearchField', true)
      }
      addUiForLayout(result, dataLayout)
      let designerPropsData = jsonForm?.designerPropsData || {}
      objectPath.set(designerPropsData, `widgetCode.${newKey}`, draggableId)
      setDesignerPropsData(designerPropsData)
    } else if (source.droppableId === 'component') {
      let dataLayout = getDataUi(layoutKey, result, staticComponent)
      objectPath.set(dataLayout, 'dataUi.path', draggableId)
      addUiForLayout(result, dataLayout)
    } else {
      if (destination.droppableId === source.droppableId) {
        reorderUiSchema(result)
      } else {
        moveSchema(result)
        moveUiSchema(result)
      }
    }
  } else {
    let layoutKey = getDestinationLayoutKey(jsonForm, current, destination)
    if (source.droppableId === 'container') {
      let found = staticContainer.find(({ id }) => id === draggableId)
      if (draggableId === 'modal') {
        // add modal wrapper
        let modal = staticContainer.find(({ id }) => id === 'modal')
        let modalLayout = getDataUi(layoutKey, result, modal)
        let modalKey = addUiForContainer(result, modalLayout)
        let mapKey = schema.mapKey[modalKey]

        // add modal body
        let modalBody = modal.uischema.part.modalBody
        modalBody.uischema.layout = layoutKey
        let modalBodyKey = 'modalBody' + generateString(5)
        addInsideModal(modalBody, mapKey, modalKey, modalBodyKey)

        // add modal footer
        let modalFooter = modal.uischema.part.modalFooter
        modalFooter.uischema.layout = layoutKey
        let modalFooterKey = 'modalFooter' + generateString(5)
        addInsideModal(modalFooter, mapKey, modalKey, modalFooterKey)

        // add button open modal
        let button = systemElement.find(({ id }) => id === 'button')
        let buttonLayout = getDataUi(layoutKey, result, {
          ...button,
          uischema: {
            ...button.uischema,
            buttonText: 'Open Modal',
            action: {
              event: 'onClick',
              name: 'openModal',
              modalType: 'internal',
              modal: {
                key: modalKey,
                data: [],
              },
              module: null,
              formId: null,
              formCode: null,
              formName: null,
            },
            modal: modalKey,
          },
        })
        addUiForContainer(result, buttonLayout)
      } else if (draggableId === 'tab') {
        // add tab
        let tab = staticContainer.find(({ id }) => id === 'tab')
        let dataLayout = getDataUi(layoutKey, result, tab, !subGroup)
        let wrapperKey = addUiForContainer(result, dataLayout)
        //

        // add tab panel
        let tabPanel = tab.uischema.part.tabPanel
        tabPanel.uischema.layout = layoutKey
        let mapKey = schema.mapKey[wrapperKey]
        let tabKey = 'tabPanel' + generateString(5)
        addInsideTab(tabPanel, mapKey, wrapperKey, tabKey)
      } else if (draggableId === 'panelGroup') {
        // add panel group
        let panelGroup = staticContainer.find(({ id }) => id === 'panelGroup')
        let dataLayout = getDataUi(layoutKey, result, panelGroup, !subGroup)
        let wrapperKey = addUiForContainer(result, dataLayout)
        //

        // add panel
        let panel = panelGroup.uischema.part.panel
        panel.uischema.layout = layoutKey
        let mapKey = schema.mapKey[wrapperKey]
        let tabKey = 'panel' + generateString(5)
        addInsideTab(panel, mapKey, wrapperKey, tabKey)
      } else if (draggableId === 'dynamic') {
        // add dynamic
        let dynamic = staticContainer.find(({ id }) => id === 'dynamic')
        let dataLayout = getDataUi(layoutKey, result, dynamic, !subGroup)
        let wrapperKey = addUiForContainer(result, dataLayout)

        // add dynamic panel
        let dynamicMapKey = schema.mapKey[wrapperKey]
        let dynamicPanel = dynamic.uischema.part.dynamicPanel
        dynamicPanel.uischema.layout = layoutKey
        let dynamicPanelKey = 'dynamicPanel' + generateString(5)
        addInsideDynamic(
          dynamicPanel,
          dynamicMapKey,
          wrapperKey,
          dynamicPanelKey,
        )
        // add edit panel
        let editPanel = dynamic.uischema.part.editPanel
        editPanel.uischema.layout = layoutKey
        let editPanelKey = 'editPanel' + generateString(5)
        addInsideDynamic(editPanel, dynamicMapKey, wrapperKey, editPanelKey)

        let newSchema = jsonForm.schema
        newSchema.mapDynamic = {
          ...newSchema.mapDynamic,
          [wrapperKey]: true,
        }
      } else {
        let dataLayout = getDataUi(layoutKey, result, found, !subGroup)
        addUiForContainer(result, dataLayout)
      }
    } else if (source.droppableId === 'widget') {
      let dataLayout = getDataUi(layoutKey, result, staticWidget)
      let newKey = objectPath.get(dataLayout, 'dataUi.key')
      let name = draggableId
        .split('-')
        .map((item, index) => (index === 0 ? item : makeUppercaseFirst(item)))
        .join('')
      let fieldName = draggableId
        .split('-')
        .map((item) => makeUppercaseFirst(item))
        .join(' ')
      objectPath.set(dataLayout, 'dataUi.path', draggableId)
      objectPath.set(dataLayout, 'dataUi.name', name)
      objectPath.set(dataLayout, 'dataUi.fieldName', fieldName)
      let widgetCode = draggableId
      let widgetJsonData = await getWidgetJsonData(
        widgetCode,
        newKey,
        designContext,
        destination.droppableId,
      )
      objectPath.set(dataLayout, 'dataUi.widget', widgetJsonData)
      if (layoutKey === 'header') {
        objectPath.set(dataLayout, 'dataUi.isSearchField', true)
      }
      addUiForContainer(result, dataLayout)
      let designerPropsData = jsonForm?.designerPropsData || {}
      objectPath.set(designerPropsData, `widgetCode.${newKey}`, draggableId)
      setDesignerPropsData(designerPropsData)
    } else if (source.droppableId === 'element') {
      let staticElement = basicElement.concat(systemElement)
      let found = staticElement.find(({ id }) => id === draggableId)
      let dataLayout = getDataUi(layoutKey, result, found)
      if (draggableId === 'checkboxGroup') {
        let elementKey = addUiForContainer(result, dataLayout)
        let editorKey = jsonForm.schema.mapKey[destination.droppableId]
        let mapName = jsonForm.schema.mapName[destination.droppableId]
        let newKey = 'checkbox' + generateString(5)
        updateUiSchema(elementKey, 'options', [
          {
            key: newKey,
            name: newKey,
            label: 'checkbox 1',
            valueChecked: true,
            valueUnchecked: false,
            defaultChecked: false,
            formData: true,
          },
        ])
        let newSchema = jsonForm.schema
        newSchema.mapTag = {
          ...newSchema.mapTag,
          [newKey]: 'checkbox',
        }
        newSchema.mapFormData = {
          ...newSchema.mapFormData,
          [newKey]: true,
        }
        newSchema.mapKey = {
          ...newSchema.mapKey,
          [newKey]: [editorKey, newKey].join('.'),
        }
        newSchema.mapName = {
          ...newSchema.mapName,
          [newKey]: [mapName, newKey].join('.'),
        }
      } else if (draggableId === 'checkboxInput') {
        let newKey = 'input' + generateString(5)
        let newName = newKey
        objectPath.set(found, 'uischema.input', {
          key: newKey,
          name: newKey,
          field: 'input',
          value: '',
          defaultValue: '',
          formData: true,
        })
        objectPath.set(found, 'uischema.inputKey', newKey)
        addUiForContainer(result, dataLayout)
        let editorKey = jsonForm.schema.mapKey[destination.droppableId]
        let mapName = jsonForm.schema.mapName[destination.droppableId]
        let newSchema = jsonForm.schema

        newSchema.mapTag = {
          ...newSchema.mapTag,
          [newKey]: 'input',
        }
        newSchema.mapFormData = {
          ...newSchema.mapFormData,
          [newKey]: true,
        }
        newSchema.mapKey = {
          ...newSchema.mapKey,
          [newKey]: [editorKey, newKey].join('.'),
        }
        newSchema.mapName = {
          ...newSchema.mapName,
          [newKey]: [mapName, newName].join('.'),
        }
      } else {
        addUiForContainer(result, dataLayout)
      }
      setSearchParam(layoutKey, dataLayout.dataUi, designContext)
    } else if (source.droppableId === 'component') {
      let dataLayout = getDataUi(layoutKey, result, staticComponent)
      objectPath.set(dataLayout, 'dataUi.path', draggableId)
      addUiForContainer(result, dataLayout)
    } else {
      if (destination.droppableId === source.droppableId) {
        reorderUiSchema(result)
      } else {
        moveSchema(result)
        moveUiSchema(result)
      }
    }
  }
}
